import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, Copy } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { AGENT_SMS } from "@/constants/testIds";

// Fix AP (2026-10-23): SMS en EE. UU. con la cuenta de Twilio de la clinica.
export default function SmsTab() {
  const [config, setConfig] = useState(null);
  const [error, setError] = useState("");
  const [sid, setSid] = useState("");
  const [token, setToken] = useState("");
  const [from, setFrom] = useState("");
  const [service, setService] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const { toast } = useToast();

  const apply = (data) => {
    setConfig(data);
    setSid(data.account_sid || "");
    setFrom(data.from_number || "");
    setService(data.messaging_service_sid || "");
    setToken("");
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/sms/config");
      apply(data); setError("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo cargar la configuración de SMS");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const fail = (title, err) => toast({ title, description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });

  const save = async () => {
    setSaving(true);
    try {
      const body = { account_sid: sid.trim(), from_number: from.trim(), messaging_service_sid: service.trim() };
      if (token.trim()) body.auth_token = token.trim();
      const { data } = await api.put("/sms/config", body);
      apply(data); setResult(null);
      toast({ title: "Datos de SMS guardados" });
    } catch (err) {
      fail("No se pudo guardar", err);
    } finally {
      setSaving(false);
    }
  };

  const patch = async (values, title) => {
    try {
      const { data } = await api.put("/sms/config", values);
      setConfig((prev) => ({ ...prev, ...data }));
      toast({ title });
    } catch (err) {
      fail("No se pudo guardar", err);
    }
  };

  const test = async () => {
    setTesting(true); setResult(null);
    try {
      const body = { account_sid: sid.trim(), from_number: from.trim(), messaging_service_sid: service.trim() };
      if (token.trim()) body.auth_token = token.trim();
      const { data } = await api.post("/sms/test", body);
      setResult(data);
    } catch (err) {
      setResult({ ok: false, message: formatApiErrorDetail(err.response?.data?.detail) || "No se pudo probar la conexión", warnings: [] });
    } finally {
      setTesting(false);
    }
  };

  const webhook = config?.webhook_url || (config?.webhook_path ? `${window.location.origin}${config.webhook_path}` : "");
  const copyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(webhook);
      toast({ title: "Dirección copiada" });
    } catch {
      toast({ title: "No se pudo copiar", description: "Seleccioná la dirección y copiala a mano.", variant: "destructive" });
    }
  };

  if (error) return <p className="text-sm text-destructive" data-testid={AGENT_SMS.error}>{error}</p>;
  if (!config) return <p className="text-sm text-muted-foreground">Cargando...</p>;

  const status = config.enabled ? { label: "Activado", cls: "bg-[#E3EDE1] text-[#5B7A54]" } : config.configured ? { label: "Apagado", cls: "bg-muted text-muted-foreground" } : { label: "Sin configurar", cls: "bg-muted text-muted-foreground" };

  return (
    <div className="space-y-6 max-w-3xl" data-testid={AGENT_SMS.tab}>
      <div className="bg-white border border-border rounded-xl p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-serif text-xl">SMS en Estados Unidos</h3>
              <Badge className={`${status.cls} border-none font-normal`} data-testid={AGENT_SMS.statusBadge}>{status.label}</Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">Para escribirle por mensaje de texto a quien no usa WhatsApp o para recordatorios a números de EE. UU. Lo que contesta la persona entra al mismo chat. Se usa tu cuenta de Twilio.</p>
          </div>
          <Switch checked={config.enabled} disabled={!config.configured} onCheckedChange={(v) => patch({ enabled: v }, v ? "SMS activados" : "SMS apagados")} data-testid={AGENT_SMS.enabledToggle} />
        </div>
        {!config.configured && <p className="text-xs text-muted-foreground">Completá y guardá los datos de Twilio para poder activar los SMS.</p>}
      </div>

      <div className="bg-white border border-border rounded-xl p-5 space-y-4">
        <h3 className="font-medium">Cuenta de Twilio</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Account SID</Label>
            <Input value={sid} onChange={(e) => setSid(e.target.value)} placeholder="AC..." autoComplete="off" spellCheck={false} data-testid={AGENT_SMS.sidInput} />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Auth Token</Label>
            <Input type="password" value={token} onChange={(e) => setToken(e.target.value)} autoComplete="new-password" spellCheck={false}
              placeholder={config.auth_token_set ? `Guardado (termina en ${config.auth_token_last4})` : "Pegalo acá"} data-testid={AGENT_SMS.tokenInput} />
            <p className="text-xs text-muted-foreground mt-1">{config.auth_token_set ? "Para cambiarlo, escribí el nuevo. Si lo dejás vacío se conserva el guardado." : "Se guarda cifrado y no se vuelve a mostrar."}</p>
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Número de envío</Label>
            <Input value={from} onChange={(e) => setFrom(e.target.value)} placeholder="+1 713 555 0123" inputMode="tel" autoComplete="off" data-testid={AGENT_SMS.fromInput} />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Messaging Service SID (opcional)</Label>
            <Input value={service} onChange={(e) => setService(e.target.value)} placeholder="MG..." autoComplete="off" spellCheck={false} data-testid={AGENT_SMS.serviceInput} />
            <p className="text-xs text-muted-foreground mt-1">Si lo cargás, los SMS salen por ese servicio en vez del número.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid={AGENT_SMS.saveButton}>{saving ? "Guardando..." : "Guardar"}</Button>
          <Button variant="outline" onClick={test} disabled={testing} data-testid={AGENT_SMS.testButton}>{testing ? "Probando..." : "Probar conexión"}</Button>
        </div>
        <p className="text-xs text-muted-foreground">Probar la conexión no manda ningún SMS: solo comprueba la cuenta y el número.</p>
        {result && (
          <div className={`rounded-lg px-3 py-2.5 text-sm ${result.ok ? "bg-[#E3EDE1] text-[#44613E]" : "bg-red-50 text-red-700"}`} data-testid={AGENT_SMS.testResult}>
            <p className="flex items-start gap-2">
              {result.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
              <span className="break-words">{result.message}</span>
            </p>
            {result.ok && result.sender && <p className="text-xs mt-1 ml-6">{result.sender}{result.account_type ? ` · cuenta ${String(result.account_type).toLowerCase() === "trial" ? "de prueba" : "completa"}` : ""}</p>}
            {(result.warnings || []).map((w, i) => <p key={i} className="text-xs mt-1 ml-6 text-amber-800">{w}</p>)}
          </div>
        )}
      </div>

      <div className="bg-white border border-border rounded-xl p-5 space-y-3">
        <h3 className="font-medium">Dirección para los SMS que llegan</h3>
        {webhook ? (
          <>
            <div className="flex gap-2">
              <Input readOnly value={webhook} onFocus={(e) => e.target.select()} className="font-mono text-xs" data-testid={AGENT_SMS.webhookUrl} />
              <Button type="button" variant="outline" size="icon" className="shrink-0" onClick={copyWebhook} aria-label="Copiar dirección" data-testid={AGENT_SMS.copyButton}><Copy className="w-4 h-4" /></Button>
            </div>
            <p className="text-xs text-muted-foreground">En Twilio, en tu número (Phone Numbers &gt; Active numbers &gt; Messaging Configuration &gt; «A message comes in») elegí Webhook, método HTTP POST, y pegá esta dirección. Si usás un Messaging Service, va en Integration &gt; Incoming Messages &gt; «Send a webhook». Los avisos de entrega se piden solos al mandar cada mensaje.</p>
          </>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid={AGENT_SMS.webhookEmpty}>La dirección aparece cuando guardás los datos de Twilio.</p>
        )}
      </div>

      <div className="bg-white border border-border rounded-xl p-5 space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-medium">El agente de IA contesta por SMS</h3>
            <p className="text-sm text-muted-foreground mt-1">Apagado por defecto: lo que llega por SMS queda para el equipo. Si lo prendés, el agente responde con mensajes cortos y sin emojis, igual que en WhatsApp con las reglas de Agente IA (y el interruptor de cada chat).</p>
          </div>
          <Switch checked={config.agent_replies} onCheckedChange={(v) => patch({ agent_replies: v }, v ? "El agente contesta por SMS" : "El agente no contesta por SMS")} data-testid={AGENT_SMS.agentToggle} />
        </div>
      </div>

      <div className="bg-muted/60 rounded-xl p-5 space-y-2 text-sm text-muted-foreground" data-testid={AGENT_SMS.checklist}>
        <p className="font-medium text-foreground">Antes de mandar SMS a clientes</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Los operadores de EE. UU. bloquean los SMS de empresas sin el registro <strong>A2P 10DLC</strong> (marca y campaña aprobadas en Twilio). La aprobación puede tardar días.</li>
          <li>Mandá SMS solo a quienes aceptaron recibirlos (por ejemplo al dejar su teléfono para sacar turno) y aclará que pueden contestar STOP para no recibir más.</li>
          <li>STOP, START y HELP los contesta Twilio solo. Quien responde STOP queda marcado en el chat y no se le puede escribir por SMS hasta que mande START.</li>
          <li>Twilio cobra por cada SMS. Los mensajes con tildes (á, í, ó, ú) o emojis se cuentan por tramos más cortos (70 caracteres); por eso el agente no usa emojis por SMS.</li>
        </ul>
      </div>
    </div>
  );
}
